import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Swords, Trophy, Target, Star, Clock, Loader2, MessageSquare, RotateCcw, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { StaffBadge } from "@/components/StaffBadge";

interface RivalryRecord {
  totalPlayed: number;
  myWins: number;
  theirWins: number;
  ties: number;
}

interface ChallengeResults {
  challenge: {
    id: string;
    mode: 'live' | 'async';
    subject: string;
    topic: string | null;
    questionCount: number;
    creatorId: string;
    opponentId: string;
    status: string;
  };
  creator: {
    id: string;
    username: string;
    displayName: string;
    profilePictureUrl?: string;
    role?: string;
  };
  opponent: {
    id: string;
    username: string;
    displayName: string;
    profilePictureUrl?: string;
    role?: string;
  };
  creatorAttempt: {
    id: string;
    totalScore: number;
    correctCount: number;
    completedAt: string | null;
  } | null;
  opponentAttempt: {
    id: string;
    totalScore: number;
    correctCount: number;
    completedAt: string | null;
  } | null;
  winner: 'creator' | 'opponent' | 'tie' | null;
}

export default function ChallengeResults() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const rematchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/challenges/${id}/rematch`);
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/challenge/${data.challengeId}`);
    },
    onError: () => {
      toast({ title: "Could not create rematch", variant: "destructive" });
    },
  });

  const { data: results, isLoading } = useQuery<ChallengeResults>({
    queryKey: ['/api/challenges', id, 'results'],
    enabled: !!id,
  });

  const opponentUserId = results
    ? (user?.id === results.creator.id ? results.opponent.id : results.creator.id)
    : undefined;

  const { data: rivalry } = useQuery<RivalryRecord>({
    queryKey: ['/api/challenges/rivalry', opponentUserId],
    enabled: !!opponentUserId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/challenges/rivalry/${opponentUserId}`);
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Results not found</p>
            <Button
              onClick={() => navigate('/messages')}
              className="mt-4"
              data-testid="button-back-messages"
            >
              Back to Messages
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { challenge, creator, opponent, creatorAttempt, opponentAttempt, winner } = results;
  const isCreator = user?.id === creator.id;
  const userAttempt = isCreator ? creatorAttempt : opponentAttempt;
  const opponentUser = isCreator ? opponent : creator;
  const opponentAttemptData = isCreator ? opponentAttempt : creatorAttempt;

  // For async mode, check if both completed
  const bothCompleted = creatorAttempt?.completedAt && opponentAttempt?.completedAt;
  const showOpponentScore = challenge.mode === 'live' || bothCompleted;

  const getWinnerBadge = () => {
    if (!winner || !bothCompleted) return null;
    
    if (winner === 'tie') {
      return <Badge variant="outline" className="text-sm sm:text-lg">It's a Tie!</Badge>;
    }

    const isUserWinner = (winner === 'creator' && isCreator) || (winner === 'opponent' && !isCreator);

    return (
      <Badge variant={isUserWinner ? "default" : "outline"} className="text-sm sm:text-lg">
        {isUserWinner ? (
          <>
            <Trophy className="h-4 w-4 mr-1" />
            You Won!
          </>
        ) : (
          <>
            {opponentUser.displayName} Won!
          </>
        )}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-4">
          <div className="flex items-center gap-3">
            <Swords className="h-6 w-6 text-primary" />
            <div>
              <CardTitle data-testid="text-results-title">Challenge Results</CardTitle>
              <div className="flex gap-2 items-center mt-1">
                <Badge variant="outline" data-testid="badge-mode">
                  {challenge.mode === 'live' ? (
                    <><Clock className="h-3 w-3 mr-1" />Live</>
                  ) : (
                    <><Trophy className="h-3 w-3 mr-1" />Async</>
                  )}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {challenge.subject} {challenge.topic && `• ${challenge.topic}`}
                </span>
              </div>
            </div>
          </div>
          {getWinnerBadge()}
        </CardHeader>
      </Card>

      {/* Results Comparison */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* User's Results */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="relative h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" data-testid="text-your-results">
                <Star className="h-5 w-5" />
                Your Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {userAttempt ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Score</span>
                    <span className="text-2xl font-bold" data-testid="text-your-score">
                      {userAttempt.totalScore}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Correct</span>
                    <span className="text-lg font-medium" data-testid="text-your-correct">
                      {userAttempt.correctCount} / {challenge.questionCount}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Accuracy</span>
                      <span className="text-sm font-medium" data-testid="text-your-accuracy">
                        {Math.round((userAttempt.correctCount / challenge.questionCount) * 100)}%
                      </span>
                    </div>
                    <Progress
                      value={Math.round((userAttempt.correctCount / challenge.questionCount) * 100)}
                      className="h-2"
                    />
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-center py-4">Not completed</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Opponent's Results */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="relative h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" data-testid="text-opponent-results">
                <Target className="h-5 w-5" />
                {opponentUser.displayName}'s Results
                {opponentUser.role === 'admin' && <StaffBadge />}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {showOpponentScore && opponentAttemptData ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Score</span>
                    <span className="text-2xl font-bold" data-testid="text-opponent-score">
                      {opponentAttemptData.totalScore}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Correct</span>
                    <span className="text-lg font-medium" data-testid="text-opponent-correct">
                      {opponentAttemptData.correctCount} / {challenge.questionCount}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Accuracy</span>
                      <span className="text-sm font-medium" data-testid="text-opponent-accuracy">
                        {Math.round((opponentAttemptData.correctCount / challenge.questionCount) * 100)}%
                      </span>
                    </div>
                    <Progress
                      value={Math.round((opponentAttemptData.correctCount / challenge.questionCount) * 100)}
                      className="h-2"
                    />
                  </div>
                </>
              ) : challenge.mode === 'async' && !bothCompleted ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">
                    Waiting for {opponentUser.displayName} to complete...
                  </p>
                  <Badge variant="outline">Score Hidden</Badge>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">Not completed</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Rivalry Banner */}
      {rivalry && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mb-6"
        >
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="p-4 flex items-center gap-2 justify-center text-sm text-muted-foreground">
              <Flame className="h-4 w-4 text-orange-500 flex-shrink-0" />
              <span>
                You and <span className="font-medium text-foreground">{opponentUser.displayName}</span>{" "}
                have played {rivalry.totalPlayed === 0 ? "no" : rivalry.totalPlayed}{" "}
                {rivalry.totalPlayed === 1 ? "time" : "times"}
                {rivalry.totalPlayed === 0
                  ? ", this is your first match!"
                  : rivalry.myWins > rivalry.theirWins
                    ? `, you lead ${rivalry.myWins}-${rivalry.theirWins}${rivalry.ties > 0 ? ` (${rivalry.ties} tied)` : ""}`
                    : rivalry.theirWins > rivalry.myWins
                      ? `, they lead ${rivalry.theirWins}-${rivalry.myWins}${rivalry.ties > 0 ? ` (${rivalry.ties} tied)` : ""}`
                      : `, all tied ${rivalry.myWins}-${rivalry.theirWins}`
                }
              </span>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.35 }}
        className="flex flex-wrap gap-4 justify-center"
      >
        <Button
          onClick={() => navigate('/messages')}
          variant="outline"
          data-testid="button-back-messages"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Back to Messages
        </Button>
        {bothCompleted && (
          <Button
            onClick={() => rematchMutation.mutate()}
            disabled={rematchMutation.isPending}
            variant="outline"
            data-testid="button-rematch"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {rematchMutation.isPending ? "Creating..." : "Rematch"}
          </Button>
        )}
        <Button
          onClick={() => navigate('/dashboard')}
          data-testid="button-dashboard"
        >
          Go to Dashboard
        </Button>
      </motion.div>
    </div>
  );
}
